import { ReactNode } from 'react';
import { GraphQLTypeNameData, GRAPHQL_TYPENAMES } from 'modules/graphql/types';

export type Snippet = {
  content: string;
  createdAt: string;
  creatorUserId: string;
  description?: string;
  id: number;
  name: string;
  positionalParameters: string[];
  projectId: number;
  updatedAt: string;
  updatedBy?: string;
  updatedByUser: User;
  creatorUser: User;
};

export enum CampaignStateDefinitions {
  Aborted = 'Aborted',
  Archived = 'Archived',
  Draft = 'Draft',
  Finished = 'Finished',
  Ready = 'Ready',
  Recurring = 'Recurring',
  Running = 'Running',
  Scheduled = 'Scheduled',
}

export enum MESSAGE_MEDIUMS {
  EMAIL = 'Email',
  IN_APP = 'InApp',
  PUSH = 'Push',
  SMS = 'SMS',
  WEB_PUSH = 'WebPush',
  SNIPPET = 'Snippet',
}

export type SnippetUsage = {
  id: number;
  resultType: string;
  creatorUser: User;
  creationDate: string;
  messageMedium: MESSAGE_MEDIUMS;
  name: string;
  campaignState: CampaignStateDefinitions | null;
  campaignName: string | null;
  campaignID: number | null;
  updatedAt: string;
  updatedByUser: User;
};

export type GraphQLSnippet = Snippet & GraphQLTypeNameData<GRAPHQL_TYPENAMES>;

export type GraphQLSnippetUse = SnippetUsage & GraphQLTypeNameData<GRAPHQL_TYPENAMES>;

export enum SnippetFieldsInputIds {
  content = 'content',
  name = 'name',
  description = 'description',
  positionalParameters = 'positionalParameters',
}

// name is purposely excluded (passed separately on create; not editable thereafter)
export type SnippetFieldsInput = {
  [SnippetFieldsInputIds.content]: string;
  [SnippetFieldsInputIds.description]?: string;
  [SnippetFieldsInputIds.name]?: string; // name should not be included but is present to satisfy BE
  [SnippetFieldsInputIds.positionalParameters]: string[];
};

export enum HasCycleKeys {
  ContainsLoop = 'Snippet contains an infinite loop.',
  FailedToProcess = 'Failed to process snippet',
  InvalidSnippet = "doesn't exist in this project",
}

export enum FieldInvalidType {
  AnchorTagsWithJS = 'AnchorTagsWithJS', // has <a href="javascript:..."> or href is not valid URI
  HasCycle = 'HasCycle', // has cyclic Snippets
  HasScriptTag = 'HasScriptTag', // has script tags (could result in XSS and other types of JS shenanigans)
  IframeTagWithJS = 'IframeTagWithJS', // has frame tags with JS (content)
  IllegalCharacters = 'IllegalCharacters', // has illegal characters
  NotRenderable = 'NotRenderable', // is not a renderable snippet using handlebars
}

export enum SnippetFailures {
  DeleteFailure = 'DeleteFailure',
  FieldInvalidFailure = 'FieldInvalidFailure',
  FieldNotProvidedFailure = 'FieldNotProvidedFailure',
  FieldTooLongFailure = 'FieldTooLongFailure',
  SnippetCreationFailure = 'SnippetCreationFailure',
  SnippetIdDoesNotExistFailure = 'SnippetIdDoesNotExistFailure',
  SnippetNameAlreadyExistsFailure = 'SnippetNameAlreadyExistsFailure',
  SnippetParameterDuplicateFailure = 'SnippetParameterDuplicateFailure',
  SnippetUpdateFailure = 'SnippetUpdateFailure',
  // RBACPermissionsFailure = 'RBACPermissionsFailure',
}

export enum SnippetSuccesses {
  DeleteSuccess = 'DeleteSuccess',
}

export type SnippetId = number;

export type DeleteFailure = {
  id: number;
  __typename: SnippetFailures;
};

export type DeleteSuccess = {
  id: SnippetId;
  name: string;
  __typename: SnippetSuccesses;
};

export type FieldInvalidFailureErrorType = {
  errorMsg: string;
  __typename: FieldInvalidType;
};

export type FieldInvalidFailure = {
  field?: SnippetFields; // optional only to satisfy tslint
  errorType?: FieldInvalidFailureErrorType; // optional only to satisfy tslint
  __typename: SnippetFailures;
};

export type FieldNotProvidedFailure = {
  field: SnippetFields;
  __typename: SnippetFailures;
};

export type FieldTooLongFailure = {
  field: SnippetFields;
  __typename: SnippetFailures;
};

export type SnippetCreationFailure = {
  errorMsg: string;
  __typename: SnippetFailures;
};

export type SnippetIdDoesNotExistFailure = {
  Id: number;
  __typename: SnippetFailures;
};

export type SnippetNameAlreadyExistsFailure = {
  field: SnippetFields;
  __typename: SnippetFailures;
};

export type SnippetParameterDuplicateFailure = {
  field: SnippetFields;
  __typename: SnippetFailures;
};

export type SnippetUpdateFailure = {
  errorMsg: string;
  __typename: SnippetFailures;
};

// GraphQL Mutaion failures
export type SnippetValidationFailure = {
  field?: SnippetFields;
  Id?: number;
  errorMsg?: string;
  errorType?: FieldInvalidFailureErrorType;
  __typename: SnippetFailures;
};

export type CreateSnippetMutationResult = {
  createSnippets: {
    success: GraphQLSnippet;
    failure: SnippetValidationFailure; // switch back to an array when the mutation has been updated
  };

  cloneSnippet: {
    success: GraphQLSnippet;
    failure: SnippetValidationFailure;
  };
};

export type CreateSnippetMutationFailures =
  | FieldInvalidFailure
  | FieldNotProvidedFailure
  | FieldTooLongFailure
  | SnippetCreationFailure
  | SnippetNameAlreadyExistsFailure
  | SnippetParameterDuplicateFailure;

export type DeleteSnippetMutationResult = {
  deleteSnippets: {
    success: DeleteSuccess[];
    failure: DeleteFailure[];
  };
};

export type UpdateSnippetMutationResult = {
  updateSnippets: {
    success: GraphQLSnippet;
    failure: SnippetValidationFailure; // switch back to an array when the mutation has been updated
  };
};

export type UpdateSnippetMutationFailures =
  | FieldInvalidFailure
  | FieldNotProvidedFailure
  | FieldTooLongFailure
  | SnippetIdDoesNotExistFailure
  | SnippetParameterDuplicateFailure
  | SnippetUpdateFailure;

// the errors returned to the UI from the Mutation Response
export type ValidationData = {
  errorMsg: string | ReactNode;
  fieldId: SnippetFieldsInputIds | ''; // identifies the FormField for decorating the field with the error
  typeName: SnippetFailures;
};

export enum SnippetValidation {
  HasCycle = 'HasCycle',
  NotRenderable = 'NotRenderable',
  ValidationSuccess = 'ValidationSuccess',
}

export type ValidationFailure = {
  type: SnippetValidation;
  errorMsg: string;
};

export type SnippetValidationResponse = ValidationFailure | SnippetValidation.ValidationSuccess;

export enum RequestType {
  Create = 'create',
  Update = 'update',
}

// more specific ids for snippet editor fields, primarily used for testing
export enum SnippetFieldTestingIds {
  Content = 'snippet-content-field',
  Description = 'snippet-description-field',
  Name = 'snippet-name-field',
  PositionalParameters = 'snippet-positional-parameters-field',
}

// match the fields used by the BE for field errors
export enum SnippetFields {
  content = 'Content',
  description = 'Description',
  name = 'Name',
  positionalParameters = 'PositionalParameter',
}

export type fieldMapType = {
  [key in SnippetFields | 'default']: {
    fieldId: SnippetFieldsInputIds | '';
    maxLength?: number;
    name: string;
  };
};

export type User = {
  id: string;
  fullName: string;
  avatarUrl: string;
};

export enum InactiveState {
  Inactive = 'Inactive',
}

export type StateWithInactive = (InactiveState | CampaignStateDefinitions)[];

export enum ResultType {
  Snippet = 'Snippet',
  Campaign = 'Campaign',
  Template = 'Template',
}

export type CsvRow = {
  id: number;
  name: string;
  messageMedium: MESSAGE_MEDIUMS;
  resultType: string;
  campaignState: CampaignStateDefinitions | 'Inactive';
  updatedAt: string;
  url: string;
};
